import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ContactRequest from "./ContactRequest";
import { toast } from "sonner";
import { MessageCircle, Pencil, Flag } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, apiError, mediaUrl } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAppData } from "../../context/AppDataContext";
import { monthYear } from "../../lib/format";

export function Tag({ children, solid, testid }) {
  return (
    <span data-testid={testid}
      className={`inline-block rounded-full px-3 py-1 text-sm font-semibold ${solid ? "bg-brand text-white" : "border-[1.5px] border-navy text-navy"}`}>
      {children}
    </span>
  );
}

export function InfoChip({ children, testid }) {
  return <span className="rounded-full border-[1.5px] border-brand/70 px-3 py-1 text-sm font-medium text-navy" data-testid={testid}>{children}</span>;
}

export function SellerCard({ owner, compact }) {
  const { t, lang } = useLang();
  return (
    <div className={`flex items-center gap-3 ${compact ? "" : "rounded-2xl border border-slate-200 p-3.5"}`} data-testid="detail-advertiser">
      <div className={`${compact ? "h-10 w-10" : "h-12 w-12"} shrink-0 overflow-hidden rounded-full bg-brand/15`}>
        {owner.avatar_url ? (
          <img src={mediaUrl(owner.avatar_url)} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-lg font-extrabold text-navy">{owner.name?.[0]?.toUpperCase()}</div>
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate font-bold text-navy" data-testid="detail-advertiser-name">{owner.name}</p>
        {owner.member_since && (
          <p className="text-sm text-slate-500" data-testid="detail-member-since">{t("detail.memberSince", { date: monthYear(owner.member_since, lang) })}</p>
        )}
      </div>
    </div>
  );
}

function ReportDialog({ open, onOpenChange, listingId }) {
  const { t } = useLang();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/listings/${listingId}/report`, { reason });
      toast.success(t("report.done"));
      onOpenChange(false);
      setReason("");
    } catch (e) {
      toast.error(apiError(e, t));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-3xl border-0 p-6" data-testid="report-dialog">
        <DialogTitle className="text-xl font-extrabold text-navy">{t("report.title")}</DialogTitle>
        <DialogDescription className="text-sm text-slate-500">{t("report.sub")}</DialogDescription>
        <textarea className="field min-h-[96px]" maxLength={500} value={reason} placeholder={t("report.reasonPh")}
          onChange={(e) => setReason(e.target.value)} data-testid="report-reason-input" />
        <div className="flex gap-2.5">
          <button className="btn-outline flex-1" onClick={() => onOpenChange(false)} data-testid="report-cancel-btn">{t("mine.cancel")}</button>
          <button className="flex-1 rounded-full bg-alert py-3 font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60" disabled={busy} onClick={send} data-testid="report-send-btn">
            {t("report.send")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ActionBar({ listing, kind, onEdit }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const { requireLogin } = useAppData();
  const [reportOpen, setReportOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const contact = (audio) => async () => {
    if (!requireLogin() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/conversations", { listing_id: listing.id });
      navigate(`/mensajes/${data.id}`, { state: { audio } });
    } catch (e) {
      toast.error(e.response?.data?.detail === "blocked" ? t("msg.cantSend") : apiError(e, t));
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-100 bg-white/95 px-5 pb-safe-4 pt-3 backdrop-blur-md md:static md:mt-6 md:border-0 md:bg-transparent md:p-0">
      {kind === "property" && !listing.is_owner && <div className="mb-3"><SellerCard owner={listing.owner} compact /></div>}
      {listing.is_owner ? (
        <button className="btn-navy w-full py-4 text-lg" onClick={onEdit} data-testid="detail-edit-btn">
          <Pencil className="h-5 w-5" /> {t("detail.edit")}
        </button>
      ) : kind === "contactos" ? (
        <ContactRequest listing={listing} />
      ) : (
        <button className="btn-primary w-full py-4 text-lg" onClick={contact(false)} disabled={busy} data-testid="detail-contact-btn">
          <MessageCircle className="h-5 w-5" /> {t("detail.contact")}
        </button>
      )}
      {!listing.is_owner && (
        <button onClick={() => requireLogin() && setReportOpen(true)}
          className="mx-auto mt-2 flex items-center gap-1 text-xs text-slate-500 underline underline-offset-2 hover:text-alert" data-testid="detail-report-link">
          <Flag className="h-3 w-3" /> {t("report.link")}
        </button>
      )}
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} listingId={listing.id} />
    </div>
  );
}
